import { cn } from "../cn";

/**
 * Which colour the bar is, in roles rather than in hues.
 *
 * `series-1` … `series-5` are the same categorical ramp `BarChart` numbers
 * 1–5, so a meter beside a chart reads as part of the same figure. The three
 * named roles are for a bar that carries a *state* — a request that failed,
 * one that did not — where a series colour would say "category 3" about
 * something that is not a category.
 */
export type MeterTone =
  | "accent"
  | "critical"
  | "warning"
  | "series-1"
  | "series-2"
  | "series-3"
  | "series-4"
  | "series-5";

const FILL: Record<MeterTone, string> = {
  accent: "bg-accent",
  critical: "bg-critical",
  warning: "bg-warning",
  "series-1": "bg-chart-1",
  "series-2": "bg-chart-2",
  "series-3": "bg-chart-3",
  "series-4": "bg-chart-4",
  "series-5": "bg-chart-5",
};

export interface MeterProps {
  /** 0–1 of the track. Values outside that are clamped rather than overflowed. */
  value: number;
  /**
   * Where the bar starts, 0–1. This is what makes the same component a
   * waterfall step as well as a magnitude bar: a step that began a third of
   * the way through a request is `offset: 0.33`.
   */
  offset?: number;
  tone?: MeterTone;
  /** `h-1.5` by default; the waterfall wants a taller bar. */
  thickness?: "thin" | "thick";
  /**
   * Only pass this when the bar is the *only* statement of the figure.
   *
   * Every use so far prints the number in real text beside the bar, and a
   * second announcement of it is noise — so the default is `aria-hidden`, and
   * the bar is decoration in the strict sense: nothing here is legible only to
   * someone who can see it.
   */
  label?: string;
  className?: string;
}

/**
 * One bar in a track.
 *
 * Requests draws two of these (a request's duration against the slowest on the
 * page, a step's span against the request's clock) and Economy health draws one
 * per movement source. All three were hand-rolled as a pair of nested spans
 * with inline widths, and the three had drifted to three heights and two
 * radii.
 *
 * Not `BarChart`. That component owns a labelled, multi-row figure with its own
 * value formatting; this is the single bar that lives *inside* a row somebody
 * else is laying out, and wrapping the chart to get one bar meant fighting its
 * grid on every call site.
 *
 * **A floor of 1.5%, not zero.** A request that took under a millisecond still
 * happened, and a bar of no width reads as missing data rather than as
 * something fast. The floor is in the component so the three call sites cannot
 * disagree about it — two of them had it and one did not.
 */
export function Meter({
  value,
  offset = 0,
  tone = "accent",
  thickness = "thin",
  label,
  className,
}: MeterProps) {
  const clamp = (n: number) => Math.min(1, Math.max(0, Number.isFinite(n) ? n : 0));
  const start = clamp(offset);
  const width = Math.max(Math.min(clamp(value), 1 - start), 0.015);

  return (
    <span
      role={label === undefined ? undefined : "img"}
      aria-label={label}
      aria-hidden={label === undefined ? "true" : undefined}
      className={cn(
        "block w-full overflow-hidden rounded-full bg-sunk",
        thickness === "thick" ? "h-3" : "h-1.5",
        className,
      )}
    >
      <span
        className={cn(
          "block h-full rounded-full transition-[width,margin] duration-(--motion-settle) ease-(--ease-out)",
          FILL[tone],
        )}
        style={{
          marginInlineStart: `${String(start * 100)}%`,
          width: `${String(width * 100)}%`,
        }}
      />
    </span>
  );
}
